import { route, SandboxError, type Route } from '../adapter';
import { newId, now, type SandboxStore } from '../store';
import { quantityFor } from '../rules';
import type { PackingLine, PackingShipment } from '@/lib/supplies';

/** The packing list: built from the kit, edited per event, linked to shipments both ways. */

function headcount(store: SandboxStore): { participants?: number; teams?: number } {
  const participants = store.event.participants ?? undefined;
  const teamSize = Number.parseInt(String(store.program.teamSize ?? ''), 10);
  const explicit = /(\d+)\s+teams?/i.exec(String(store.event.afterParticipants ?? ''))?.[1];
  const teams = explicit ? Number(explicit) : participants && teamSize > 0 ? Math.ceil(participants / teamSize) : undefined;
  return { ...(participants !== undefined ? { participants } : {}), ...(teams !== undefined ? { teams } : {}) };
}

export function buildFromKit(store: SandboxStore): PackingLine[] {
  const count = headcount(store);
  return (store.program.kit ?? []).flatMap((line) => {
    const item = store.supplies.find((supply) => supply._id === line.itemId);
    if (!item) return [];
    const { quantity, needsHeadcount } = quantityFor(line.quantityRule, count);
    const built: PackingLine = {
      _id: newId(),
      itemId: item._id,
      name: item.name,
      quantity,
      ...(item.unit ? { unit: item.unit } : {}),
      ...(item.imageUrl ? { imageUrl: item.imageUrl } : {}),
      sources: item.sources,
      ...(line.optional ? { optional: true } : {}),
      ...(line.note ? { note: line.note } : {}),
      source: 'kit',
      needsHeadcount,
      shipments: [],
    };
    return [built];
  });
}

/** Keeps each shipment's items in step with the lines that say they travel in it. */
export function syncShipmentsFromLine(store: SandboxStore, line: PackingLine): void {
  const wanted = new Map((line.shipments ?? []).map((entry) => [entry.shipmentId, entry]));
  store.shipments = store.shipments.map((shipment) => {
    const items = (shipment.items ?? []).filter((item) => item.packingLineId !== line._id);
    const entry = wanted.get(shipment._id);
    if (entry) items.push({ name: line.name, quantity: entry.quantity ?? line.quantity, packingLineId: line._id });
    return { ...shipment, items };
  });
}

export const materialRoutes: readonly Route[] = [
  route('GET', '/events/:id/packing-list', ({ store }) => ({ data: store.packingList })),
  route('POST', '/events/:id/packing-list/reset', ({ store }) => {
    const manual = store.packingList.filter((line) => line.source === 'manual');
    store.packingList = [...buildFromKit(store), ...manual];
    store.shipments = store.shipments.map((shipment) => ({ ...shipment, items: (shipment.items ?? []).filter((item) => !item.packingLineId || manual.some((line) => line._id === item.packingLineId)) }));
    return { data: store.packingList };
  }),
  route('POST', '/events/:id/packing-list', ({ store, body }) => {
    const write = body as Partial<PackingLine> & { itemId?: string; name?: string; quantity?: number };
    const item = write.itemId ? store.supplies.find((supply) => supply._id === write.itemId) : undefined;
    if (!item && !write.name) throw new SandboxError(400, 'An item or a name is required.');
    const line: PackingLine = {
      _id: newId(),
      ...(item ? { itemId: item._id, name: item.name, sources: item.sources, ...(item.unit ? { unit: item.unit } : {}), ...(item.imageUrl ? { imageUrl: item.imageUrl } : {}) } : { name: write.name ?? '' }),
      quantity: write.quantity ?? 1,
      ...(write.unit ? { unit: write.unit } : {}),
      ...(write.note ? { note: write.note } : {}),
      ...(write.optional ? { optional: true } : {}),
      ...(write.clientProvides ? { clientProvides: true } : {}),
      source: 'manual',
      shipments: [],
    };
    store.packingList = [...store.packingList, line];
    return { data: store.packingList };
  }),
  route('PATCH', '/events/:id/packing-list/:lineId', ({ store, params, body }) => {
    const patch = body as Partial<PackingLine> & { packed?: boolean; shipments?: PackingShipment[] };
    const index = store.packingList.findIndex((line) => line._id === params['lineId']);
    const existing = store.packingList[index];
    if (!existing) throw new SandboxError(404, 'Packing line not found.');
    const { packed, ...rest } = patch;
    const updated: PackingLine = { ...existing, ...rest, ...(packed === undefined ? {} : packed ? { packedAt: now() } : { packedAt: undefined }) };
    store.packingList[index] = updated;
    if (patch.shipments) syncShipmentsFromLine(store, updated);
    return { data: store.packingList };
  }),
  route('DELETE', '/events/:id/packing-list/:lineId', ({ store, params }) => {
    store.packingList = store.packingList.filter((line) => line._id !== params['lineId']);
    store.shipments = store.shipments.map((shipment) => ({ ...shipment, items: (shipment.items ?? []).filter((item) => item.packingLineId !== params['lineId']) }));
    return { data: store.packingList };
  }),
];
